import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Download, BookOpen, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Lead-magnet landing page for "De Complete Koopplanner Spanje".
 *
 * This page is intentionally NOT in the main nav — it exists as a
 * destination for QR codes on flyers/business cards + as a link inside
 * blog articles. Keeps the funnel clean: someone hits this URL, they
 * are here for one reason only, we don't distract them with the rest of
 * the site.
 *
 * SEO: noindex to avoid ranking this above the "real" content. The QR is
 * the entry point.
 */
const GratisKoopplanner = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  // GDPR — consent must be an active choice, NOT pre-checked.
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !consent) return;
    setStatus({ state: 'loading', message: '' });
    try {
      const r = await fetch(`${API}/api/lead-magnets/koopplanner-spanje/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || null, consent }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus({ state: 'success', message: `Sprawdź swoją skrzynkę (${email}) — przewodnik jest w drodze.` });
    } catch (err) {
      setStatus({ state: 'error', message: `Coś poszło nie tak: ${err.message}. Spróbuj ponownie.` });
    }
  };

  return (
    <div style={styles.wrap} data-testid="koopplanner-landing">
      <Helmet>
        <title>Bezpłatny planer zakupu w Hiszpanii — CasaOthers</title>
        <meta name="description" content="Otrzymaj bezpłatnie kompletny planer zakupu nieruchomości w Hiszpanii. Plan krok po kroku, listy kontrolne, kalkulator kosztów i więcej." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://casaothers.pl/gratis-koopplanner-spanje" />
      </Helmet>

      <div style={styles.hero}>
        <div style={styles.tag}>
          <Sparkles size={14} /> Bezpłatne pobranie
        </div>
        <h1 style={styles.h1}>Kompletny planer zakupu w Hiszpanii</h1>
        <p style={styles.lead}>
          Zebraliśmy wszystko, co musisz wiedzieć o zakupie nieruchomości w Hiszpanii —
          od numeru NIE po notariusza — w jednym praktycznym PDF-ie. Oszczędza godziny researchu
          i pomaga uniknąć kosztownych błędów.
        </p>
      </div>

      <div style={styles.grid}>
        {/* Left: what's inside */}
        <div style={styles.left}>
          <h2 style={styles.h2}><BookOpen size={20} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} /> Co zawiera?</h2>
          <ul style={styles.list}>
            <li>Proces zakupu krok po kroku — od oglądania po przekazanie kluczy</li>
            <li>Kompletny kalkulator kosztów (podatki, notariusz, prowizja)</li>
            <li>Lista kontrolna <em>due diligence</em>: co sprawdzić przed podpisaniem</li>
            <li>Wniosek o numer NIE: wszystkie dokumenty i gdzie się zgłosić</li>
            <li>Kredyt hipoteczny w Hiszpanii: ile możesz pożyczyć jako obcokrajowiec?</li>
            <li>Najczęstsze błędy i jak ich unikać</li>
          </ul>
          <div style={styles.trust}>
            <div><strong>25+ stron</strong><br/>praktycznej treści</div>
            <div><strong>Format PDF</strong><br/>prosto na skrzynkę</div>
            <div><strong>Bezpłatnie</strong><br/>bez haczyków</div>
          </div>
        </div>

        {/* Right: signup form */}
        <div style={styles.right}>
          {status.state === 'success' ? (
            <div style={styles.successBox} data-testid="signup-success">
              <CheckCircle2 size={40} color="#166534" />
              <h3 style={styles.successTitle}>Dziękujemy!</h3>
              <p style={styles.successText}>{status.message}</p>
              <p style={styles.successHint}>Nie widzisz go? Sprawdź folder spam.</p>
            </div>
          ) : (
            <form onSubmit={submit} style={styles.form}>
              <h2 style={styles.formTitle}>Odbierz swój przewodnik</h2>
              <label style={styles.label} htmlFor="name">Imię (opcjonalnie)</label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                style={styles.input}
                placeholder="Twoje imię"
                data-testid="signup-name"
              />
              <label style={styles.label} htmlFor="email">Adres e-mail <span style={styles.req}>*</span></label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={styles.input}
                placeholder="ty@przyklad.com"
                data-testid="signup-email"
              />
              <label style={styles.checkbox}>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                  data-testid="signup-consent"
                  required
                />
                <span>
                  Tak, wyślijcie mi przewodnik oraz <strong>od czasu do czasu najnowsze oferty nieruchomości i praktyczne porady</strong> o rynku w Hiszpanii.
                  Akceptuję{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#C5A059', textDecoration: 'underline' }}>
                    politykę prywatności
                  </a>{' '}
                  CasaOthers. W każdej chwili można wypisać się jednym kliknięciem na dole każdej wiadomości.
                </span>
              </label>
              <button
                type="submit"
                disabled={!email || !consent || status.state === 'loading'}
                style={styles.button}
                data-testid="signup-submit"
              >
                <Download size={18} />
                {status.state === 'loading' ? 'Wysyłanie…' : 'Wyślijcie mi przewodnik'}
              </button>
              {status.state === 'error' && (
                <p style={styles.errorText}>{status.message}</p>
              )}
              <p style={styles.legalese}>
                Twoje dane są traktowane poufnie. Zobacz naszą{' '}
                <a href="/privacy" style={{ color: '#C5A059' }}>politykę prywatności</a>.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrap: { maxWidth: '1100px', margin: '0 auto', padding: '3rem 1.5rem 6rem' },
  hero: { textAlign: 'center', maxWidth: '780px', margin: '0 auto 3rem' },
  tag: {
    display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
    padding: '0.35rem 0.9rem', backgroundColor: '#FEF3C7', color: '#78350F',
    border: '1px solid #FCD34D', borderRadius: '999px', fontSize: '0.8rem',
    fontWeight: 600, marginBottom: '1.25rem',
  },
  h1: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2rem, 5vw, 3.4rem)',
    color: '#1A1918', margin: '0 0 1rem', lineHeight: 1.15,
  },
  lead: { color: '#585755', fontSize: '1.1rem', lineHeight: 1.6, margin: 0 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '2.5rem',
    alignItems: 'start',
  },
  left: {},
  h2: { fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', color: '#1A1918', margin: '0 0 1rem' },
  list: {
    color: '#1A1918', fontSize: '1rem', lineHeight: 1.75,
    paddingLeft: '1.2rem', margin: '0 0 2rem',
  },
  trust: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem',
    padding: '1.25rem', backgroundColor: '#FAF9F6',
    border: '1px solid #EAE5DD',
    fontSize: '0.85rem', color: '#585755', textAlign: 'center',
  },
  right: {},
  form: {
    padding: '2rem', backgroundColor: '#1A1918', color: 'white',
    border: '1px solid #1A1918',
  },
  formTitle: { fontFamily: "'Playfair Display', serif", fontSize: '1.5rem', margin: '0 0 1.25rem', color: '#C5A059' },
  label: { display: 'block', fontSize: '0.82rem', color: '#B8B8B4', margin: '0.75rem 0 0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' },
  req: { color: '#C5A059' },
  input: {
    width: '100%', boxSizing: 'border-box',
    padding: '0.85rem 1rem', fontSize: '1rem',
    backgroundColor: '#2A2928', border: '1px solid #3A3936', color: 'white',
    fontFamily: 'inherit', outline: 'none',
  },
  checkbox: {
    display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
    fontSize: '0.85rem', color: '#B8B8B4', margin: '1.25rem 0 1.5rem',
    lineHeight: 1.5, cursor: 'pointer',
  },
  button: {
    width: '100%', padding: '1rem', border: 'none',
    backgroundColor: '#C5A059', color: '#1A1918',
    fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.08em',
    textTransform: 'uppercase', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
    transition: 'opacity 0.2s',
  },
  errorText: { color: '#FCA5A5', fontSize: '0.85rem', margin: '1rem 0 0' },
  legalese: { color: '#8C8B88', fontSize: '0.75rem', margin: '1rem 0 0', lineHeight: 1.5 },
  successBox: {
    padding: '2.5rem', textAlign: 'center', backgroundColor: '#F0FDF4',
    border: '1px solid #BBF7D0',
  },
  successTitle: { fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#166534', margin: '1rem 0 0.5rem' },
  successText: { color: '#166534', fontSize: '1rem', margin: '0 0 0.5rem' },
  successHint: { color: '#4B7B5E', fontSize: '0.85rem', margin: '1rem 0 0' },
};

export default GratisKoopplanner;
